// src/pages/admin/GalleryAdmin.jsx - COM EDIÇÃO INLINE (SEM PUT)
import React, { useState, useEffect, useContext } from 'react';
import { ConfigContext } from '../../contexts/ConfigContext';
import axios from 'axios';
import { Trash2, Plus, Image as ImageIcon, Loader, Check, Edit2 } from 'lucide-react';

const GalleryAdmin = () => {
  const { API_URL } = useContext(ConfigContext);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Estados para o modal de upload
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDescription, setUploadDescription] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingCategory, setPendingCategory] = useState('');
  
  // Estados para edição
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editDescription, setEditDescription] = useState('');

  useEffect(() => {
    loadPhotos();
  }, [API_URL]);

  const loadPhotos = async () => {
    try {
      const res = await axios.get(`${API_URL}/gallery`);
      setPhotos(res.data);
    } catch (err) {
      console.error('Erro ao carregar fotos:', err);
    } finally {
      setLoading(false);
    }
  };

  // Abre o modal para adicionar descrição antes do upload
  const handleFileSelect = (e, category) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setPendingFile(file);
    setPendingCategory(category);
    setUploadDescription('');
    setShowUploadModal(true);
  };

  // Confirma o upload com a descrição
  const confirmUpload = async () => {
    if (!uploadDescription.trim()) {
      alert('Por favor, adicione uma descrição para a foto!');
      return;
    }

    const formData = new FormData();
    formData.append('photo', pendingFile);
    formData.append('category', pendingCategory);
    formData.append('title', uploadDescription);

    setUploading(true);
    try {
      await axios.post(`${API_URL}/upload-gallery`, formData);
      await loadPhotos();
      showSuccessToast('Foto adicionada com sucesso!');
      setShowUploadModal(false);
      setPendingFile(null);
      setUploadDescription('');
    } catch (err) {
      alert('Erro ao fazer upload: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const cancelUpload = () => {
    setShowUploadModal(false);
    setPendingFile(null);
    setUploadDescription('');
  };

  // Abre modal de edição
  const openEditModal = (photo) => {
    setEditingPhoto(photo);
    setEditDescription(photo.title);
    setShowEditModal(true);
  };

  const cancelEdit = () => {
    setShowEditModal(false);
    setEditingPhoto(null);
    setEditDescription('');
  };

  // Salva a edição (converte imagem URL em Blob e faz novo upload)
  const confirmEdit = async () => {
    if (!editDescription.trim()) {
      alert('A descrição não pode estar vazia!');
      return;
    }

    setUploading(true);
    try {
      // 1. Baixar a imagem atual como Blob
      const imageUrl = getImageUrl(editingPhoto.image_url);
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // 2. Criar um File a partir do Blob
      const fileName = editingPhoto.image_url.split('/').pop();
      const file = new File([blob], fileName, { type: blob.type });
      
      // 3. Fazer upload da nova foto com a descrição atualizada
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('category', editingPhoto.category);
      formData.append('title', editDescription);
      
      await axios.post(`${API_URL}/upload-gallery`, formData);
      
      // 4. Deletar a foto antiga
      await axios.delete(`${API_URL}/gallery/${editingPhoto.id}`);
      
      // 5. Recarregar galeria
      await loadPhotos();
      
      showSuccessToast('Descrição atualizada com sucesso!');
      setShowEditModal(false);
      setEditingPhoto(null);
      setEditDescription('');
    } catch (err) {
      console.error('Erro ao editar:', err);
      alert('Erro ao atualizar descrição: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta foto?')) return;

    try {
      await axios.delete(`${API_URL}/gallery/${id}`);
      await loadPhotos();
      showSuccessToast('Foto excluída com sucesso!');
    } catch (err) {
      alert('Erro ao deletar: ' + (err.response?.data?.error || err.message));
    }
  };

  const showSuccessToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) {
      return path;
    }
    const baseUrl = 'https://api.davillanoronha.com.br';
    let cleanPath = path.startsWith('/') ? path.slice(1) : path;
    if (!cleanPath.startsWith('uploads/') && !cleanPath.startsWith('assets/')) {
      cleanPath = `uploads/${cleanPath}`;
    }
    return `${baseUrl}/${cleanPath}`;
  };

  const filteredPhotos = selectedCategory === 'all' 
    ? photos 
    : photos.filter(p => p.category === selectedCategory);

  const categories = [
    { key: 'all', label: 'Todas', count: photos.length },
    { key: 'pousada', label: 'Pousada', count: photos.filter(p => p.category === 'pousada').length },
    { key: 'quartos', label: 'Quartos', count: photos.filter(p => p.category === 'quartos').length },
    { key: 'arredores', label: 'Arredores', count: photos.filter(p => p.category === 'arredores').length }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Loader className="spin" size={32} color="#0d9488" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{
        display:'flex', 
        justifyContent:'space-between', 
        alignItems:'center', 
        marginBottom:'2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h2 style={{color: '#1e293b', margin:0}}>Galeria de Fotos</h2>
          <p style={{color: '#64748b', fontSize: '0.9rem', marginTop: '0.5rem'}}>
            📝 Adicione descrições detalhadas e edite quando necessário
          </p>
        </div>
      </div>

      {/* Filtros de Categoria */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        padding: '1rem',
        background: 'white',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        {categories.map(cat => (
          <button
            key={cat.key}
            onClick={() => setSelectedCategory(cat.key)}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: selectedCategory === cat.key ? '#0d9488' : '#f8fafc',
              color: selectedCategory === cat.key ? 'white' : '#475569',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
          >
            {cat.label}
            <span style={{
              background: selectedCategory === cat.key ? 'rgba(255,255,255,0.3)' : '#e2e8f0',
              padding: '0.25rem 0.5rem',
              borderRadius: '12px',
              fontSize: '0.8rem',
              fontWeight: '600'
            }}>
              {cat.count}
            </span>
          </button>
        ))}
      </div>

      {/* Botão de Upload */}
      {selectedCategory !== 'all' && (
        <div style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ marginBottom: '1rem', color: '#1e293b' }}>
            Adicionar foto em: {categories.find(c => c.key === selectedCategory)?.label}
          </h3>
          <label style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '0.75rem 1.5rem',
            background: '#0d9488',
            color: 'white',
            borderRadius: '6px',
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            opacity: uploading ? 0.6 : 1
          }}>
            {uploading ? <Loader className="spin" size={18} /> : <Plus size={18} />}
            {uploading ? 'Enviando...' : 'Adicionar Foto'}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => handleFileSelect(e, selectedCategory)}
              disabled={uploading}
            />
          </label>
        </div>
      )}

      {/* Grid de Fotos */}
      {filteredPhotos.length === 0 ? (
        <div style={{
          padding: '4rem 2rem',
          textAlign: 'center',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <ImageIcon size={64} style={{ margin: '0 auto 1rem', opacity: 0.3, color: '#cbd5e1' }} />
          <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>Nenhuma foto encontrada</h3>
          <p style={{ color: '#94a3b8' }}>
            {selectedCategory === 'all' 
              ? 'Adicione fotos usando os botões acima'
              : `Adicione fotos na categoria ${categories.find(c => c.key === selectedCategory)?.label}`
            }
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredPhotos.map((photo) => (
            <div
              key={photo.id}
              style={{
                background: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #e2e8f0',
                transition: 'all 0.2s',
                position: 'relative'
              }}
              className="photo-card-hover"
            >
              {/* Imagem */}
              <div style={{
                position: 'relative',
                paddingBottom: '66.67%',
                background: '#f8fafc',
                overflow: 'hidden'
              }}>
                <img
                  src={getImageUrl(photo.image_url)}
                  alt={photo.title}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                
                {/* Badge da Categoria */}
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  left: '8px',
                  padding: '0.25rem 0.75rem',
                  background: 'rgba(13, 148, 136, 0.9)',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {photo.category}
                </div>

                {/* Botão Delete */}
                <button
                  onClick={() => handleDelete(photo.id)}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    padding: '8px',
                    background: 'rgba(239, 68, 68, 0.9)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  className="delete-btn"
                  title="Excluir foto"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Área de Descrição com Botão de Editar */}
              <div style={{ padding: '1rem', background: '#f8fafc' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: '0.875rem',
                    color: '#475569',
                    lineHeight: '1.5',
                    flex: 1
                  }}>
                    {photo.title}
                  </p>
                  <button
                    onClick={() => openEditModal(photo)}
                    style={{
                      padding: '0.5rem',
                      background: '#0d9488',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                    title="Editar descrição"
                  >
                    <Edit2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL DE UPLOAD */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ marginTop: 0, color: '#1e293b', marginBottom: '1rem' }}>
              📝 Adicionar Descrição
            </h3>
            <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Esta descrição será exibida junto com a foto na galeria pública.
            </p>
            
            {pendingFile && (
              <div style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                background: '#f0fdf4',
                borderRadius: '6px',
                fontSize: '0.85rem',
                color: '#166534',
                border: '1px solid #bbf7d0'
              }}>
                <strong>📎 Arquivo:</strong> {pendingFile.name}
              </div>
            )}
            
            <textarea
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              placeholder="Ex: Vista panorâmica da pousada ao pôr do sol..."
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '0.95rem',
                resize: 'vertical',
                minHeight: '100px',
                fontFamily: 'inherit',
                marginBottom: '1.5rem'
              }}
              autoFocus
            />
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={confirmUpload}
                disabled={uploading || !uploadDescription.trim()}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: '#0d9488',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (uploading || !uploadDescription.trim()) ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  opacity: (uploading || !uploadDescription.trim()) ? 0.6 : 1
                }}
              >
                {uploading ? (
                  <><Loader className="spin" size={18} /> Enviando...</>
                ) : (
                  <><Plus size={18} /> Adicionar Foto</>
                )}
              </button>
              <button
                onClick={cancelUpload}
                disabled={uploading}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: '#64748b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  opacity: uploading ? 0.6 : 1
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE EDIÇÃO */}
      {showEditModal && editingPhoto && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ marginTop: 0, color: '#1e293b', marginBottom: '1rem' }}>
              ✏️ Editar Descrição
            </h3>
            
            {/* Preview da imagem */}
            <div style={{
              marginBottom: '1rem',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #e2e8f0'
            }}>
              <img 
                src={getImageUrl(editingPhoto.image_url)} 
                alt={editingPhoto.title}
                style={{
                  width: '100%',
                  height: '150px',
                  objectFit: 'cover'
                }}
              />
            </div>
            
            <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Descrição atual: <strong>{editingPhoto.title}</strong>
            </p>
            
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Digite a nova descrição..."
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '0.95rem',
                resize: 'vertical',
                minHeight: '100px',
                fontFamily: 'inherit',
                marginBottom: '1.5rem'
              }}
              autoFocus
            />
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={confirmEdit}
                disabled={uploading || !editDescription.trim()}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (uploading || !editDescription.trim()) ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  opacity: (uploading || !editDescription.trim()) ? 0.6 : 1
                }}
              >
                {uploading ? (
                  <><Loader className="spin" size={18} /> Salvando...</>
                ) : (
                  <>💾 Salvar Alterações</>
                )}
              </button>
              <button
                onClick={cancelEdit}
                disabled={uploading}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: '#64748b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  opacity: uploading ? 0.6 : 1
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {showToast && (
        <div style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          background: '#10b981',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000
        }}>
          <Check size={20} />
          <span style={{ fontWeight: '500' }}>{toastMessage}</span>
        </div>
      )}

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .photo-card-hover:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transform: translateY(-2px);
        }
        .delete-btn:hover {
          background: rgba(220, 38, 38, 0.9) !important;
        }
      `}</style>
    </div>
  );
};

export default GalleryAdmin;